// frontend/src/services/mixpanel.ts
import mixpanel from 'mixpanel-browser';
import { isNativePlatform } from './platform';

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_PROJECT_TOKEN;
let isMixpanelInitialized = false;

// Crawlers execute the SPA without persisting storage, so every page they
// fetch mints a fresh anonymous device id and inflates unique-user counts.
// Never initialize analytics for automated clients.
const BOT_UA_PATTERN =
  /bot|crawl|spider|slurp|bingpreview|headless|lighthouse|phantomjs|puppeteer|playwright|prerender|facebookexternalhit|whatsapp|telegram|discordbot|pingdom|uptime|monitor/i;

const isAutomatedClient = (): boolean => {
  try {
    if (typeof navigator === 'undefined') return true;
    if ((navigator as { webdriver?: boolean }).webdriver) return true;
    return BOT_UA_PATTERN.test(navigator.userAgent || '');
  } catch {
    return false;
  }
};

export const mixpanelInit = (): void => {
  if (isAutomatedClient()) {
    console.log('Mixpanel skipped: automated client detected.');
    return;
  }
  if (MIXPANEL_TOKEN && !isMixpanelInitialized) {
    try {
      mixpanel.init(MIXPANEL_TOKEN, {
        debug: import.meta.env.DEV, // Enable debug mode in development
        track_pageview: false, // We'll handle page views manually for better control with React Router
        persistence: 'localStorage',
        // We do not use location analytics. Prevent Mixpanel from deriving
        // city/region/country from the request IP address.
        ip: false,
        // You can add more config here if needed, e.g.:
        // api_host: "https://api-eu.mixpanel.com", // If your data is in EU
      });
      isMixpanelInitialized = true;
      // Super property on every event so native-app engagement can be
      // segmented from web. The native build only fires at all when the
      // token is baked in at build time (frontend/.env.local for local
      // iOS builds; Render env for the static site).
      mixpanel.register({ platform: isNativePlatform() ? 'ios-app' : 'web' });
      console.log('Mixpanel SDK Initialized (Frontend)');
    } catch (error) {
      console.error('Mixpanel SDK Initialization Failed (Frontend):', error);
      isMixpanelInitialized = false;
    }
  } else if (!MIXPANEL_TOKEN) {
    console.warn('Mixpanel token not found (VITE_MIXPANEL_PROJECT_TOKEN). Mixpanel will not be initialized on frontend.');
  } else if (isMixpanelInitialized) {
    console.log('Mixpanel already initialized (Frontend).');
  }
};

export const mixpanelIdentify = (userId: string | number): void => {
  if (!isMixpanelInitialized) {
    // console.debug('Mixpanel not initialized, skipping identify.');
    return;
  }
  const idStr = String(userId);
  try {
    mixpanel.identify(idStr);
    console.log('Mixpanel identified signed-in user.');
  } catch (error) {
    console.error('Mixpanel identify error:', error);
  }
};

export const mixpanelTrack = (eventName: string, properties?: Record<string, any>): void => {
  if (!isMixpanelInitialized) {
    // console.debug(`Mixpanel not initialized, skipping track: ${eventName}`);
    return;
  }
  try {
    // Create a new object for all properties, including the new event_source
    const allProperties = {
      ...properties, // Spread existing properties first
      event_source: 'frontend', // Add our new property
    };
    mixpanel.track(eventName, allProperties); // Send all properties
    console.log(`Mixpanel tracked event: ${eventName}`, allProperties); // Log all properties
  } catch (error) {
    console.error(`Mixpanel track error for event ${eventName}:`, error);
  }
};


export const mixpanelPeopleSet = (properties: Record<string, any>): void => {
  if (!isMixpanelInitialized) {
    // console.debug('Mixpanel not initialized, skipping people.set.');
    return;
  }
  try {
    mixpanel.people.set(properties);
    console.log('Mixpanel people.set:', properties);
  } catch (error) {
    console.error('Mixpanel people.set error:', error);
  }
};

export const mixpanelPeopleSetOnce = (properties: Record<string, any>): void => {
  if (!isMixpanelInitialized) {
    // console.debug('Mixpanel not initialized, skipping people.set_once.');
    return;
  }
  try {
    mixpanel.people.set_once(properties);
    console.log('Mixpanel people.set_once:', properties);
  } catch (error) {
    console.error('Mixpanel people.set_once error:', error);
  }
};

export const mixpanelReset = (): void => {
  if (!isMixpanelInitialized) {
    // console.debug('Mixpanel not initialized, skipping reset.');
    return;
  }
  try {
    mixpanel.reset(); // Clears super properties and generates a new random distinct_id
    console.log('Mixpanel reset');
  } catch (error) {
    console.error('Mixpanel reset error:', error);
  }
};

// Alias is useful for connecting anonymous activity to a known user.
// Call this *once* after a user signs up or logs in for the first time
// if you want to merge pre-login activity.
// `originalId` is the anonymous distinct_id Mixpanel generated before identify.
// If `originalId` is not provided, Mixpanel uses the current distinct_id.
export const mixpanelAlias = (newUserId: string | number, originalAnonymousId?: string): void => {
  if (!isMixpanelInitialized) return;
  const newIdStr = String(newUserId);
  try {
    if (originalAnonymousId && originalAnonymousId !== newIdStr) {
      mixpanel.alias(newIdStr, originalAnonymousId);
      console.log('Mixpanel alias created for signed-in user.');
    } else {
      // If no originalAnonymousId, or it's the same as newUserId,
      // then alias might not be needed or could cause issues.
      // Just ensure identify is called.
      console.log('Mixpanel alias not needed; identify remains authoritative.');
    }
  } catch (error) {
    console.error('Mixpanel alias error:', error);
  }
};

// Capture UTM parameters from the URL and persist them as super properties + people properties
export const captureUtmParams = (): void => {
  if (!isMixpanelInitialized) return;
  try {
    const params = new URLSearchParams(window.location.search);
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    const utmProps: Record<string, string> = {};
    for (const key of utmKeys) {
      const value = params.get(key);
      if (value) {
        utmProps[key] = value;
      }
    }
    if (Object.keys(utmProps).length > 0) {
      // Register as super properties so they persist across all events in this session
      mixpanel.register(utmProps);
      // Set as people properties (set_once = first-touch attribution sticks)
      mixpanel.people.set_once(utmProps);
      console.log('Mixpanel UTM params captured:', utmProps);
    }
  } catch (error) {
    console.error('Mixpanel UTM capture error:', error);
  }
};

// Register super properties (properties sent with every event from this client)
export const mixpanelRegister = (properties: Record<string, any>): void => {
    if (!isMixpanelInitialized) return;
    try {
        mixpanel.register(properties);
        console.log('Mixpanel super properties registered:', properties);
    } catch (error) {
        console.error('Mixpanel register (super properties) error:', error);
    }
};

// Increment a numeric people property (e.g., 'Total Lineups Submitted')
export const mixpanelPeopleIncrement = (property: string, by: number = 1): void => {
    if (!isMixpanelInitialized) return;
    try {
        mixpanel.people.increment(property, by);
        console.log(`Mixpanel people.increment: ${property} by ${by}`);
    } catch (error) {
        console.error(`Mixpanel people.increment error for ${property}:`, error);
    }
};

// Unregister super properties
export const mixpanelUnregister = (propertyName: string): void => {
    if (!isMixpanelInitialized) return;
    try {
        mixpanel.unregister(propertyName);
        console.log(`Mixpanel super property unregistered: ${propertyName}`);
    } catch (error) {
        console.error(`Mixpanel unregister (super property) error for ${propertyName}:`, error);
    }
};
